import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';

import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import AddIcon from '@mui/icons-material/Add';
import { Box, IconButton, Tooltip } from '@mui/material';

import { observer } from 'mobx-react';

import { colors } from 'theme/colors';
import { useStore } from 'utils/hooks/store';

import ScrollControls from 'components/ScrollControls';
import TruncatedText from 'components/TruncatedText';
import { useRowOverflow } from 'hooks/useRowOverflow';

import { MAX_PIPELINE_STAGES } from '../../constants';

import SortableStage from './SortableStage';
import {
  ClosedStage,
  ClosedStageWrapper,
  Name,
  STAGE_PILL_HEIGHT,
  StagesRow,
  StagesViewport,
  StyledDivider,
} from './styles';

const StagesDragAndDrop = (): React.ReactElement => {
  const { pipelineDetails } = useStore();

  // Names, not just the count: a rename can overflow the row without resizing it.
  const stageNames = (pipelineDetails.data?.stages ?? []).map(({ name }) => name);
  const { rowRef, overflow, measure, scrollBy } = useRowOverflow<HTMLDivElement>(stageNames);

  const stages = pipelineDetails.data?.stages ?? [];
  const order = pipelineDetails.data?.order ?? [];

  const stageLimitReached = order.length >= MAX_PIPELINE_STAGES;

  const closedStage = stages.find((stage) => stage.closed);
  const closedStageKey = closedStage?.key;
  const closedStageName = closedStage?.name ?? '';
  const selectedClosedStage = pipelineDetails.selectedStage === closedStageKey;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = order.findIndex((item) => item === active.id);
      const newIndex = order.findIndex((item) => item === over?.id);

      pipelineDetails.updateOrder(arrayMove(order, oldIndex, newIndex));
    }
  };

  const selectClosedStage = (): void => {
    if (closedStageKey) {
      pipelineDetails.setSelectedStage(closedStageKey);
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: '16px' }}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToHorizontalAxis]}
      >
        <SortableContext items={order} strategy={horizontalListSortingStrategy}>
          <StagesViewport $share={order.length - 1}>
            <StagesRow ref={rowRef} onScroll={measure}>
              {order.map((stageKey) => (
                <React.Fragment key={stageKey}>
                  <SortableStage stageKey={stageKey} />
                </React.Fragment>
              ))}
            </StagesRow>
            <ScrollControls overflow={overflow} onScroll={scrollBy} height={STAGE_PILL_HEIGHT} />
          </StagesViewport>
        </SortableContext>
      </DndContext>

      <StyledDivider orientation="vertical" />
      {closedStageKey && (
        <ClosedStageWrapper>
          <TruncatedText<HTMLParagraphElement> text={closedStageName}>
            {(closedNameRef) => (
              <ClosedStage $isSelected={selectedClosedStage} onClick={selectClosedStage}>
                <Name ref={closedNameRef} $isSelected={selectedClosedStage}>
                  {closedStageName}
                </Name>
              </ClosedStage>
            )}
          </TruncatedText>
        </ClosedStageWrapper>
      )}

      {/* A disabled button fires no mouse events, so the tooltip hangs off a wrapper. */}
      <Tooltip title={stageLimitReached ? `A pipeline can have up to ${MAX_PIPELINE_STAGES} stages` : ''}>
        <span>
          <IconButton
            disabled={stageLimitReached}
            style={{
              padding: 0,
              margin: 0,
              width: '44px',
              height: '44px',
              backgroundColor: stageLimitReached ? colors.divider : colors.common.white,
            }}
            onClick={() => pipelineDetails.setIsModalOpenForCreatingStage(true)}
          >
            <AddIcon sx={{ color: colors.primary.main }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default observer(StagesDragAndDrop);
